import { groupGamesByDay } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type { GameLike } from '@sportninja/common/features/game/gameListView/types'
import { todayCivilDate as viewerCivilDate } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'
import { bucketEventsByDay } from '@sportninja/common/features/event/calendarItems'
import type { SnEvent } from '@sportninja/common/features/event/types'
import { DateTime } from 'luxon'

import type { Game } from '../game-list-row/types'
import type { CalendarDay } from './types'

const UTC = 'UTC'

/**
 * Civil "today" in the viewer's own zone.
 *
 * Used for BOTH the highlight and the "Today" buttons. Deriving the button from
 * `new Date().toISOString()` (a UTC date) while highlighting a local one meant
 * that on a weekday evening in the Americas, pressing Today jumped to tomorrow
 * and no cell was highlighted.
 */
export const todayKey = (): string => viewerCivilDate()

/** Alias with the intent spelled out, for the navigation buttons. */
export const todayCivilDate = todayKey

interface DayBuckets {
  games: Map<string, Game[]>
  tbd: Map<string, Game[]>
}

/**
 * Buckets games by VENUE-LOCAL civil day.
 *
 * The previous implementation keyed on `dayjs(starts_at).date()`, i.e. the
 * BROWSER's day. An admin in Toronto looking at a Vancouver schedule saw the
 * 7pm games land on the following day, and the calendar disagreed with the list
 * on the same dataset.
 */
export const bucketGames = (items: Game[]): DayBuckets => {
  const games = new Map<string, Game[]>()
  const tbd = new Map<string, Game[]>()

  for (const bucket of groupGamesByDay(items as unknown as GameLike[])) {
    if (bucket.games.length > 0) {
      games.set(bucket.dayKey, bucket.games as unknown as Game[])
    }
    if (bucket.tbdGames.length > 0) {
      tbd.set(bucket.dayKey, bucket.tbdGames as unknown as Game[])
    }
  }

  return { games, tbd }
}

const day = (
  dayKey: string,
  dayNumber: number,
  isCurrentMonth: boolean,
  buckets: DayBuckets,
  today: string,
  eventsByDay: Map<string, SnEvent[]>
): CalendarDay => ({
  dayKey,
  dayNumber,
  isCurrentMonth,
  isToday: dayKey === today,
  games: buckets.games.get(dayKey) ?? [],
  tbdGames: buckets.tbd.get(dayKey) ?? [],
  //  Keyed in the EVENT's own zone by `bucketEventsByDay`, matching how
  //  `groupGamesByDay` keys games venue-locally — so a 7pm event and a 7pm
  //  game at the same place land in the same cell.
  events: eventsByDay.get(dayKey) ?? [],
})

/** Always 42 cells, so the grid never reflows as months change length. */
export const buildMonthDays = (
  anchor: DateTime,
  items: Game[],
  events?: SnEvent[]
): CalendarDay[] => {
  const buckets = bucketGames(items)
  const eventsByDay = bucketEventsByDay(events)
  const today = todayKey()

  const firstOfMonth = anchor.startOf('month')
  //  Luxon's `weekday` is 1=Mon..7=Sun; the grid starts on Sunday.
  const leading = firstOfMonth.weekday % 7
  const gridStart = firstOfMonth.minus({ days: leading })

  return Array.from({ length: 42 }, (_, index) => {
    const date = gridStart.plus({ days: index })
    return day(
      date.toFormat('yyyy-MM-dd'),
      date.day,
      date.month === anchor.month && date.year === anchor.year,
      buckets,
      today,
      eventsByDay
    )
  })
}

export const buildWeekDays = (
  anchor: DateTime,
  items: Game[],
  events?: SnEvent[]
): CalendarDay[] => {
  const buckets = bucketGames(items)
  const eventsByDay = bucketEventsByDay(events)
  const today = todayKey()
  const weekStart = anchor.minus({ days: anchor.weekday % 7 })

  return Array.from({ length: 7 }, (_, index) => {
    const date = weekStart.plus({ days: index })
    return day(
      date.toFormat('yyyy-MM-dd'),
      date.day,
      true,
      buckets,
      today,
      eventsByDay
    )
  })
}

export const civilDate = (value: string): DateTime =>
  DateTime.fromISO(value, { zone: UTC })

/** Formats a civil day key without ever re-projecting it into a zone. */
export const formatCivilDay = (dayKey: string, format: string): string =>
  civilDate(dayKey).toFormat(format)
